/**
 * Help — v6.32.100
 * Dedicated /help page hosting the MOI bot full-screen alongside a
 * curated FAQ topic list and direct links to the Platform Guide and
 * support email. Signed-in only.
 */
import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Bot, BookOpen, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { MoiHelpBotPanel } from '@/components/MoiHelpBot';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'mosaic_session_token';

export default function Help() {
  const { user, loading } = useAuth();
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem(TOKEN_KEY);
    axios
      .get(`${API}/api/help-bot/topics`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setTopics(res.data.topics || []))
      .catch(() => setTopics([]));
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: '/help' }} />;
  // v6.36.16 — Students must NEVER see the MOI Help bot. Redirect to
  // their dashboard if they navigate here directly.
  if (user.role === 'student') return <Navigate to="/my-assessment" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="help-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-red-600 mb-2">
          <Bot size={14} />
          <span className="font-semibold uppercase tracking-wide">Help Center</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-black mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Hi, I'm MOI.
        </h1>
        <p className="text-base text-gray-600 max-w-2xl">
          MOSAIC's friendly helper. Ask me anything about the platform — BLM downloads,
          the Teacher Portal, Content Studio, the digital exam, or whatever you need.
          For pricing, email <a href="mailto:sales@mosaicassessmentco.com" className="underline text-red-600 hover:text-red-700">sales@mosaicassessmentco.com</a> or
          click "Get a Quote" — the sales team handles that directly. I keep answers short and
          link to the full Platform Guide for step-by-step.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat (2 columns) */}
        <div className="lg:col-span-2">
          <MoiHelpBotPanel embedded={true} />
        </div>

        {/* Sidebar (1 column) */}
        <div className="space-y-4">
          <section className="border border-gray-200 rounded-md p-5 bg-white" data-testid="help-page-topics">
            <h2 className="text-base font-bold mb-3 flex items-center gap-2">
              <BookOpen size={16} /> What I know about
            </h2>
            <ul className="space-y-1.5 text-sm text-gray-700">
              {topics.map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <span className="w-1 h-1 bg-red-600 rounded-full flex-shrink-0" />
                  {t}
                </li>
              ))}
              {topics.length === 0 && (
                <li className="text-gray-400 italic">Loading topics…</li>
              )}
            </ul>
          </section>

          <section className="border border-gray-200 rounded-md p-5 bg-white">
            <h2 className="text-base font-bold mb-3">Need a deep dive?</h2>
            <p className="text-sm text-gray-600 mb-3">
              The Platform Guide has full step-by-step walkthroughs of every feature.
            </p>
            <Link
              to="/guide"
              className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700"
              data-testid="help-page-guide-link"
            >
              Open Platform Guide <ArrowRight size={14} />
            </Link>
          </section>

          <section className="border border-gray-200 rounded-md p-5 bg-amber-50 border-amber-200">
            <h2 className="text-base font-bold mb-2 flex items-center gap-2">
              <Mail size={16} /> Talk to a human
            </h2>
            <p className="text-sm text-amber-900 mb-3">
              MOI doesn't know everything yet. For anything sensitive, complex, or off-topic,
              email our team.
            </p>
            <a
              href="mailto:support@mosaicassessmentco.com"
              className="inline-flex items-center gap-1 text-sm font-semibold text-amber-900 hover:text-amber-700"
              data-testid="help-page-support-email"
            >
              support@mosaicassessmentco.com <ArrowRight size={14} />
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}
